import React, { useCallback, useEffect, useMemo } from 'react';
import * as Yup from 'yup';
import { NextPage } from 'next';
import { Grid } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'i18n';
import { useInfinityLoader, useQueryParams } from 'hooks';
import { EventsActions, OrganizationsActions } from 'store/actions';
import { OrganizationsSelectors } from 'store/selectors';
import { getErrorFromStates, isFetchingStates } from 'store/utils';
import withGetInitialPropsRouteValidation from 'hocs/withGetInitialPropsRouteValidation';
import withRouteValidation from 'hocs/withRouteValidation';
import { UserHeader } from 'components/common/Header';
import { TabTitle } from 'components/common/TabTitle';
import EventsScreen from 'components/screens/EventsScreen';
import FilterPanel from 'components/filters/FilterPanel';
import EventTargetAgesFilter from 'components/filters/EventTargetAgesFilter';
import EventTypesFilter from 'components/filters/EventTypesFilter';
import EventTopicsFilter from 'components/filters/EventTopicsFilter';
import PriceFilter from 'components/filters/PriceFilter';
import ClearFiltersButton from 'components/filters/ClearFiltersButton';
import SortFilter from 'components/filters/SortFilter';
import DatesFilter from 'components/filters/DatesFilter';
import RenderComponent from 'components/common/RenderComponent';
import { RootState } from 'store/rootReducer';
import OrgEventsTop from './OrgEventsTop';
import LocalMarketFilter from '../filters/LocalMarketFilter';

interface IProps {
  organizationId: number;
  sortType?: 'events' | 'alphabetical' | 'pastEvents';
}

const querySchema = Yup.object({
  organizationId: Yup.number().required(),
});

const withOrganizationEvents = (
  titleKey: string,
  actions: any,
  makeStateSelector: any,
  makePaginationSelector: any,
  defaultOrder: string,
  sortType: 'events' | 'alphabetical' | 'pastEvents' = 'events',
) => {
  const OrganizationEventsPage: NextPage<IProps, any> = ({ organizationId }) => {
    const dispatch = useDispatch();
    const [t] = useTranslation('public');

    const organizationState = useSelector((state: RootState) =>
      OrganizationsSelectors.organizationStateById(state, organizationId),
    );
    const [queryParams] = useQueryParams();

    const hiddenQueryParams = useMemo(
      () => ({
        organization: organizationId,
        ordering: queryParams?.ordering || defaultOrder,
        page_size: 12,
        include_suborganizations: Boolean(organizationState.entity?.settings?.can_include_suborganizations_events),
      }),
      [organizationId, organizationState.entity?.settings, queryParams?.ordering],
    );

    const handleToggleFavoriteEvent = useCallback(
      (eventId: any) => dispatch(EventsActions.toggleFavoriteEvent.request({ id: eventId })),
      [dispatch],
    );

    const stateSelector: any = useMemo(makeStateSelector, []);
    const paginationSelector: any = useMemo(makePaginationSelector, []);
    const {
      state: eventsState,
      setElementChild,
      setElementWrapper,
    } = useInfinityLoader({
      actions,
      stateSelector,
      paginationSelector,
      options: {
        skipInitialLoad: false,
        hiddenQueryParams,
        queryParams,
      },
    });

    useEffect(() => {
      setElementWrapper(window);
      setElementChild(document.documentElement);
    }, [setElementChild, setElementWrapper]);

    return (
      <>
        {organizationState.entity && (
          <TabTitle
            ns="public"
            i18nKey="tab.upcomingPastEvents"
            signLabel="general"
            type={t(titleKey)}
            organization={organizationState.entity.title}
          />
        )}
        <UserHeader />
        <RenderComponent
          layout
          isLoading={isFetchingStates(organizationState)}
          errors={getErrorFromStates(organizationState)}
          renderContent={() => (
            <EventsScreen
              events={eventsState.entities}
              isEventsFetching={eventsState.isFetching}
              onToggleFavoriteEvent={handleToggleFavoriteEvent}
              top={
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <OrgEventsTop organization={organizationState.entity} title={t(titleKey)} />
                  </Grid>
                  <Grid item xs={12}>
                    <FilterPanel
                      sort={
                        <SortFilter disabled={eventsState.isFetching} defaultOrder={defaultOrder} type={sortType} />
                      }
                    >
                      <ClearFiltersButton disabled={eventsState.isFetching} />
                      <EventTypesFilter disabled={eventsState.isFetching} />
                      <EventTopicsFilter disabled={eventsState.isFetching} />
                      <EventTargetAgesFilter disabled={eventsState.isFetching} />
                      <PriceFilter disabled={eventsState.isFetching} />
                      <LocalMarketFilter disabled={eventsState.isFetching} />
                      <DatesFilter disabled={eventsState.isFetching} />
                    </FilterPanel>
                  </Grid>
                </Grid>
              }
            />
          )}
        />
      </>
    );
  };

  // todo change to getServerSide
  OrganizationEventsPage.getInitialProps = withGetInitialPropsRouteValidation(
    querySchema,
    async ({ store, query }: any) => {
      const organizationId = Number(query.organizationId);

      if (Number.isInteger(organizationId)) {
        store.dispatch(OrganizationsActions.getOrganization.request({ id: organizationId }));

        return {
          organizationId,
        };
      }

      return {};
    },
  );

  return withRouteValidation(OrganizationEventsPage);
};

export default withOrganizationEvents;
